import {requestFormDataPost, requestGet, requestPost, requestPut} from "../util/request";
import {store} from '../store';
import {setSnackbarOpen} from "../store/taskSlice";
import {NoticeProps} from "../types/common.types";
import {ImagePricingRule} from "../types/member.type";

interface UploadResponse {
    code: number;
    data: any;
    message: string;
}

interface NoticeResponse {
    code: number;
    data: NoticeProps[];
    message: string;
    success: boolean
}

interface NoticeDetailResponse {
    code: number;
    data: NoticeProps;
    message: string;
    success: boolean
}

interface NotifyUnreadCountResponse {
    code: number;
    message: string;
    data?: {
        unreadCount: number;
    };
    timestamp: string;
}

interface NotifyMessagesReadResponse {
    code: number;
    message: string;
    data?: {
        modifiedCount: number;
    };
    timestamp: string;
}

interface NotifyMessagesResponse {
    code: number;
    message: string;
    data?: {
        items: NotifyMessageItem[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };
    timestamp: string;
}

interface ImagePricingRulesResponse {
    success: boolean;
    data: ImagePricingRule[];
}

export interface NotifyMessagesQuery {
    is_read?: boolean;
    page?: number;
    limit?: number;
}

export interface NotifyMessagesPageResult {
    items: NotifyMessageItem[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export interface NotifyMessageItem {
    id: string;
    type: string;
    title: string;
    content: string;
    is_read: boolean;
    status: string;
    related_url: string;
    createdAt: string;
}

interface ChangeSetDataResponse {
    id: number,
    fileId: string,
    conversionId: string,
    revision: number,
    eventData: string,
    eventType: string,
    createdAt: string,
    updatedAt: string
}

interface ChangeSetResponse {
    code: number;
    data: ChangeSetDataResponse[];
    message: string;
}

export const uploadCanvasJson = async (clientId: string, canvasJson: string): Promise<any> => {
    try {
        const response = await requestPost(
            `/api/canvas?id=${clientId}`,
            { data: canvasJson }
        ) as UploadResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        throw new Error(message);
    } catch (error) {
        throw error instanceof Error ? error : new Error('Upload failed');
    }
};

export const uploadImageTags = async (uuid: string, tags: string[]): Promise<any> => {
    try {
        const response = await requestPost(
            `/api/mood/updateImageTag?imageUuid=${uuid}`,
            tags
        ) as UploadResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        throw new Error(message);
    } catch (error) {
        throw error instanceof Error ? error : new Error('Upload failed');
    }
};

export const getChangeSet = async (filedId: string, versionId?: number, specifyVersions?: string, tag?: string, step?: number): Promise<ChangeSetDataResponse[]> => {
    try {
        const response = await requestGet(
            `/api/mood/changeSet?fileId=${filedId}${versionId === undefined ? '' : `&fromVersion=${versionId}`}&specifyVersions=${specifyVersions}&tag=${tag}${step ? `&step=${step}` : ''}`,
        ) as ChangeSetResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return [];
    } catch (error) {
        throw error instanceof Error ? error : new Error('Upload failed');
    }
};

export const getRemoteCanvasJson = async (clientId?: string): Promise<any> => {
    try {
        const response = await requestGet(
            `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/canvas/${clientId}`, true
        ) as UploadResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('Upload failed');
    }
}

export const uploadLocalImage = async (clientId: string, file: File): Promise<UploadResponse> => {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await requestFormDataPost(
            '/api/mood/upload',
            formData
        ) as UploadResponse;

        const { code, data, message } = response;

        if (code === 200) {
            return data;
        }else {
            store.dispatch(setSnackbarOpen({open: true, message}))
        }

        throw new Error(message);
    } catch (error) {
        throw error instanceof Error ? error : new Error('图片上传失败');
    }
}

export const getAnnouncements = async (): Promise<NoticeProps[] | null> => {
    try {
        const response = await requestGet(
            `/kanvas-api/api/v1/announcements/enabled`, true
        ) as NoticeResponse;



        if (response) {
            const { code, data, message, success } = response;

            if (success) {
                return data;
            }
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getImagePricingRules = async (): Promise<ImagePricingRule[]> => {
    try {
        const response = await requestGet(
            `/kanvas-api/api/v1/image-pricing/rules`, true
        ) as ImagePricingRulesResponse | null;

        if (response?.success && Array.isArray(response.data)) {
            return response.data;
        }

        return [];
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getAnnouncementsById = async (id: string): Promise<NoticeProps | null> => {
    try {
        const response = await requestGet(
            `/kanvas-api/api/v1/announcements/${id}`, true
        ) as NoticeDetailResponse;



        if (response) {
            const { code, data, message, success } = response;

            if (success) {
                return data;
            }
        }

        return null;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getNotifyUnreadCount = async (): Promise<number> => {
    try {
        const response = await requestGet(
            '/kanvas-api/api/v1/notify-messages/unread-count',
            true
        ) as NotifyUnreadCountResponse | null;

        if (response?.code === 200) {
            return response.data?.unreadCount || 0;
        }

        return 0;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const markNotifyMessagesRead = async (messageIds: string[]): Promise<number> => {
    try {
        const response = await requestPut(
            '/kanvas-api/api/v1/notify-messages/read',
            { message_ids: messageIds },
            true
        ) as NotifyMessagesReadResponse | null;

        if (response?.code === 200) {
            return response.data?.modifiedCount || 0;
        }

        return 0;
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

export const getNotifyMessages = async (query: NotifyMessagesQuery = {}): Promise<NotifyMessagesPageResult> => {
    try {
        const params = new URLSearchParams();
        if (query.is_read !== undefined) params.set('is_read', String(query.is_read));
        if (query.page !== undefined) params.set('page', String(query.page));
        if (query.limit !== undefined) params.set('limit', String(query.limit));
        const queryString = params.toString();

        const response = await requestGet(
            `/kanvas-api/api/v1/notify-messages${queryString ? `?${queryString}` : ''}`,
            true
        ) as NotifyMessagesResponse | null;

        if (response?.code === 200 && response.data?.items) {
            return {
                items: response.data.items,
                pagination: response.data.pagination ?? {
                    page: query.page ?? 1,
                    limit: query.limit ?? response.data.items.length,
                    total: response.data.items.length,
                    totalPages: 1
                }
            };
        }

        return {
            items: [],
            pagination: {
                page: query.page ?? 1,
                limit: query.limit ?? 0,
                total: 0,
                totalPages: 0
            }
        };
    } catch (error) {
        throw error instanceof Error ? error : new Error('network error');
    }
}

interface SurveySubmitResponse {
    code: number;
    message: string;
    data?: {
        id: number;
    };
}

export const submitSurvey = async (data: {
    channel: string;
    industry: string;
    contact?: string;
}, token?: string): Promise<SurveySubmitResponse | null> => {
    try {
        const url = `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/v1/survey/submit`;
        if (token) {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'K-Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            return await res.json() as SurveySubmitResponse;
        }
        return await requestPost(url, data, true) as SurveySubmitResponse | null;
    } catch (error) {
        console.error('submitSurvey error:', error);
        return null;
    }
};

interface SurveyRecord {
    id: string;
    userUuid?: string;
    description?: string | null;
    channel: string;
    industry: string;
    contact?: string;
    ipAddress?: string;
    location?: string | null;
    createdAt: string;
}

interface SurveyRecordsResponse {
    code: number;
    message: string;
    data?: {
        items: SurveyRecord[];
        total: number;
        page: number;
        pageSize: number;
    };
}

export const getSurveyRecords = async (page: number = 1, pageSize: number = 10, token?: string): Promise<SurveyRecordsResponse | null> => {
    try {
        const url = `${process.env.REACT_APP_APPLICATION_MAIN_INTERFACE}/api/v1/survey/records?page=${page}&pageSize=${pageSize}`;
        if (token) {
            const res = await fetch(url, {
                headers: {
                    'K-Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            return await res.json() as SurveyRecordsResponse;
        }
        return await requestGet(url, true) as SurveyRecordsResponse | null;
    } catch (error) {
        console.error('getSurveyRecords error:', error);
        return null;
    }
};

/* ---- CMS 内容管理 ---- */

export interface CmsItem {
    id: string;
    type: 'video' | 'blog' | 'mix' | 'mix_single';
    source: 'bilibili' | 'blog';
    title: string;
    summary?: string;
    previewImageUrl: string;
    smallTitle?: string;
    smallPreviewImageUrl?: string;
    externalUrl: string;
    slug?: string;
    durationSeconds?: number;
    readMinutes?: number;
    publishedAt: string;
    tags?: string[];
    priority?: number;
    isRead: boolean;
}

interface CmsItemsResponse {
    code: number;
    message: string;
    data: CmsItem[];
}

/** 获取 CMS 内容列表 */
export async function fetchCmsItems(): Promise<CmsItem[]> {
    try {
        const base = process.env.REACT_APP_APPLICATION_MAIN_INTERFACE ?? '';
        const res = await requestGet(`${base}/api/cms/items`, true) as CmsItemsResponse | null;
        if (res?.code === 200 && Array.isArray(res.data)) {
            return res.data;
        }
        return [];
    } catch {
        return [];
    }
}

export interface RecommendationItem {
    id: string;
    type: CmsItem['type'];
    source: CmsItem['source'];
    title: string;
    summary?: string;
    coverUrl: string;
    coverAspectRatio?: string;
    previewImageUrl: string;
    width?: number;
    height?: number;
    smallTitle?: string;
    smallPreviewImageUrl?: string;
    externalUrl: string;
    slug?: string;
    durationSeconds?: number;
    readMinutes?: number;
    publishedAt: string;
    tags?: string[];
    priority?: number;
    presentationKey: 'large' | 'small' | 'default';
}

export const RECOMMENDATION_SLOT_CODES = {
    HOME_AUTOPLAY: 'home_autoplay',
    HOME_TOP: 'home_top',
    HOME_CAROUSEL: 'home_carousel',
    HOME_BLOG_FEED: 'home_blog_feed',
    INSPIRE_CARD_RECOMMEND: 'inspire_card_recommend',
} as const;

export type RecommendationSlotCode = typeof RECOMMENDATION_SLOT_CODES[keyof typeof RECOMMENDATION_SLOT_CODES];

interface RecommendationResolveResponse {
    code: number;
    message: string;
    data: {
        slotCode: string;
        resolvedSlotCode: string | null;
        algorithmCode: string | null;
        requestId: string;
        items: RecommendationItem[];
    };
}

export async function resolveRecommendationSlot(
    slotCode: RecommendationSlotCode,
    excludeItemIds: string[] = [],
): Promise<RecommendationItem[]> {
    try {
        const base = process.env.REACT_APP_APPLICATION_MAIN_INTERFACE ?? '';
        const res = await requestPost<RecommendationResolveResponse>(
            `${base}/api/cms/recommendations/resolve`,
            { slotCode, excludeItemIds },
            true,
        );
        if (res?.code === 200 && Array.isArray(res.data?.items)) {
            return res.data.items;
        }
        return [];
    } catch {
        return [];
    }
}
